import { ArrowBack } from '@mui/icons-material';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Text, Textarea } from 'rizzui';
import { toast } from 'sonner';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import PageHeader from '../../../layout/shared/page-header';
import { AuthState } from '../../../redux/authSlice';
import { RootState } from '../../../redux/store';
import { menuRoutes } from '../../../router/menu';
import { CommunicationService } from '../../../services/communication/communicationService';
import { emitCommunicationAlertsRefresh } from '../../../utils/communicationAlerts';
import { ContractProfileLocationState } from '../../../utils/contractNavigation';

type CommunicationDetailLocationState = {
    returnToCommunicationsPath?: string;
};

const getCommunicationRecord = (payload: any) => {
    if (payload?.data && !Array.isArray(payload.data)) return payload.data;
    return payload ?? null;
};

const formatDateValue = (value: any): string => {
    if (!value) return '-';
    if (typeof value === 'string') return value;
    if (typeof value?.date === 'string') return value.date;
    return '-';
};

const CommunicationDetailPage = () => {
    const { id = '' } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const locationState = (location.state as CommunicationDetailLocationState | null) ?? null;
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const { user }: AuthState = useSelector((state: RootState) => state.auth);
    const [replyMessage, setReplyMessage] = useState('');
    const [isSubmittingReply, setIsSubmittingReply] = useState(false);
    const service = new CommunicationService();

    const returnToCommunicationsPath = locationState?.returnToCommunicationsPath || `${menuRoutes.calls.path}${menuRoutes.calls.communications}`;

    const [response, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id) return null;
        const apiResponse = await service.getCommunication(id);
        return apiResponse.getResponseData();
    }, [id]));
    const communication = useMemo(() => getCommunicationRecord(response), [response]);

    const canReplyToCommunication = useMemo(() => {
        return Boolean(
            communication
            && !communication.closedAt
            && userCan('finish_communications', 'communications')
            && user?.department
            && communication.targetDepartment?.id === user.department
        );
    }, [communication, user?.department, userCan]);

    const contractProfileState: ContractProfileLocationState = {
        fromCommunications: true,
        returnToCommunicationsPath,
    };

    const contractProfilePath = communication?.contract?.id
        ? `${menuRoutes.contracts.path}/${communication.contract.id}/profile${menuRoutes.contracts.profile.info}`
        : null;

    const handleReply = async () => {
        const message = replyMessage.trim();
        if (!communication?.id) return;

        if (!message) {
            toast.error('Debes informar el mensaje de cierre.');
            return;
        }

        try {
            setIsSubmittingReply(true);
            const apiResponse = await (await service.replyCommunication({
                communicationId: communication.id,
                message,
            })).getResponseData();

            if (!apiResponse?.success) {
                handleErrors(apiResponse);
                return;
            }

            toast.success('Comunicación cerrada correctamente');
            setReplyMessage('');
            refetch();
            emitCommunicationAlertsRefresh();
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsSubmittingReply(false);
        }
    };

    if (loading && !communication) {
        return <Loader height="50vh" />;
    }

    return (
        <Page container="fluid">
            <PageHeader
                title={
                    <span className="flex flex-wrap items-center gap-2">
                        Comunicación
                        {communication?.contract?.code && (
                            <span className="text-gray-500">· {communication.contract.code}</span>
                        )}
                        {communication && (
                            <span
                                className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                                    communication.closedAt
                                        ? 'bg-gray-100 text-gray-700'
                                        : 'bg-amber-100 text-amber-800'
                                }`}
                            >
                                {communication.closedAt ? 'Cerrada' : 'Abierta'}
                            </span>
                        )}
                    </span>
                }
                breadcrumb={[
                    { name: 'Ventas' },
                    { name: 'Gestión de campañas' },
                    { name: 'Comunicaciones', href: returnToCommunicationsPath },
                    { name: 'Detalle' },
                ]}
            >
                <div className="mt-3 flex flex-wrap items-center gap-3 md:mt-0">
                    <Button
                        variant="outline"
                        className="bg-primary text-white hover:bg-transparent hover:text-secondary hover:border-muted"
                        onClick={() => navigate(returnToCommunicationsPath)}
                    >
                        <ArrowBack /> Volver a comunicaciones
                    </Button>
                </div>
            </PageHeader>

            {!loading && !communication && (
                <section className="rounded-lg border border-gray-200 bg-white px-6 py-12 text-center">
                    <Text className="text-base font-semibold text-gray-900">No se ha podido cargar la comunicación</Text>
                    <Text className="mt-2 text-sm text-gray-500">
                        {error instanceof Error && error.message
                            ? error.message
                            : 'La comunicación no existe, no está disponible o no tienes acceso a su contenido.'}
                    </Text>
                    <div className="mt-6 flex justify-center">
                        <Button
                            variant="outline"
                            onClick={() => navigate(returnToCommunicationsPath)}
                        >
                            Volver a comunicaciones
                        </Button>
                    </div>
                </section>
            )}

            {!loading && communication && (
                <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_360px]">
                    <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <Text className="text-base font-semibold text-gray-900">Detalle de la comunicación</Text>
                        </div>

                        <div className="space-y-6 px-6 py-6">
                            <article className="rounded-lg border border-sky-100 bg-sky-50 px-5 py-4">
                                <Text className="text-xs font-semibold uppercase tracking-wide text-sky-700">
                                    Mensaje de apertura
                                </Text>
                                <Text className="mt-3 whitespace-pre-wrap text-sm leading-7 text-gray-700">
                                    {communication?.openingMessage || 'Sin mensaje de apertura'}
                                </Text>
                            </article>

                            {communication?.closedAt ? (
                                <article className="rounded-lg border border-emerald-100 bg-emerald-50 px-5 py-4">
                                    <Text className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                                        Respuesta y cierre
                                    </Text>
                                    <Text className="mt-3 whitespace-pre-wrap text-sm leading-7 text-gray-700">
                                        {communication?.closingMessage || 'Sin mensaje de cierre'}
                                    </Text>
                                </article>
                            ) : (
                                <article className="rounded-lg border border-amber-100 bg-amber-50 px-5 py-4">
                                    <Text className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                                        Estado actual
                                    </Text>
                                    <Text className="mt-3 text-sm leading-7 text-gray-700">
                                        Esta comunicación sigue abierta.
                                    </Text>
                                </article>
                            )}

                            {canReplyToCommunication ? (
                                <div className="rounded-lg border border-gray-200 bg-white px-5 py-5">
                                    <Text className="text-sm font-semibold text-gray-900">Responder y cerrar</Text>
                                    <Text className="mt-1 text-sm text-gray-500">
                                        Añade la respuesta final para resolver la comunicación.
                                    </Text>
                                    <div className="mt-4 space-y-4">
                                        <Textarea
                                            value={replyMessage}
                                            onChange={(event) => setReplyMessage(event.target.value)}
                                            rows={6}
                                            placeholder="Escribe la respuesta final..."
                                        />
                                        <div className="flex justify-end">
                                            <Button onClick={handleReply} isLoading={isSubmittingReply}>
                                                Cerrar comunicación
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-lg border border-gray-200 bg-gray-50 px-5 py-4">
                                    <Text className="text-sm text-gray-600">
                                        {communication?.closedAt
                                            ? 'Esta comunicación ya está cerrada y no admite más respuestas.'
                                            : 'Tienes acceso de solo lectura sobre esta comunicación.'}
                                    </Text>
                                </div>
                            )}
                        </div>
                    </section>

                    <aside className="space-y-6">
                        <section className="rounded-lg border border-gray-200 bg-white">
                            <div className="border-b border-gray-200 px-5 py-4">
                                <Text className="text-sm font-semibold text-gray-900">Información general</Text>
                            </div>
                            <div className="space-y-4 px-5 py-5">
                                <div>
                                    <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500">Contrato</Text>
                                    <Text className="mt-1 text-sm text-gray-900">{communication?.contract?.code || 'Sin contrato'}</Text>
                                </div>
                                <div>
                                    <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500">Alumno</Text>
                                    <Text className="mt-1 text-sm text-gray-900">
                                        {[communication?.contract?.studentName, communication?.contract?.studentFirstName, communication?.contract?.studentLastName]
                                            .filter(Boolean)
                                            .join(' ') || 'Sin alumno'}
                                    </Text>
                                </div>
                                <div>
                                    <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500">Departamento destino</Text>
                                    <Text className="mt-1 text-sm text-gray-900">
                                        {communication?.targetDepartment?.name || communication?.targetDepartmentLegacy || 'N/A'}
                                    </Text>
                                </div>
                                <div>
                                    <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500">Abierta por</Text>
                                    <Text className="mt-1 text-sm text-gray-900">
                                        {[communication?.openedByUser?.name, communication?.openedByUser?.lastName].filter(Boolean).join(' ') || 'Sin emisor'}
                                    </Text>
                                </div>
                                <div>
                                    <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500">Fecha de apertura</Text>
                                    <Text className="mt-1 text-sm text-gray-900">
                                        {formatDateValue(communication?.openedAt || communication?.createdAt)}
                                    </Text>
                                </div>
                                <div>
                                    <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500">Cerrada por</Text>
                                    <Text className="mt-1 text-sm text-gray-900">
                                        {[communication?.closedByUser?.name, communication?.closedByUser?.lastName].filter(Boolean).join(' ') || 'Pendiente'}
                                    </Text>
                                </div>
                                <div>
                                    <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500">Fecha de cierre</Text>
                                    <Text className="mt-1 text-sm text-gray-900">
                                        {formatDateValue(communication?.closedAt)}
                                    </Text>
                                </div>
                            </div>
                        </section>

                        {contractProfilePath && userCan('get_contracts', 'contracts') && (
                            <section className="rounded-lg border border-gray-200 bg-white px-5 py-5">
                                <Text className="text-sm font-semibold text-gray-900">Contrato relacionado</Text>
                                <Text className="mt-2 text-sm text-gray-500">
                                    Accede al perfil del contrato manteniendo la navegación de vuelta a comunicaciones.
                                </Text>
                                <div className="mt-4">
                                    <Button
                                        variant="outline"
                                        color="secondary"
                                        onClick={() => navigate(contractProfilePath, { state: contractProfileState })}
                                    >
                                        Abrir contrato
                                    </Button>
                                </div>
                            </section>
                        )}
                    </aside>
                </div>
            )}
        </Page>
    );
};

export default CommunicationDetailPage;
